import { COMMENT_QUOTE_MAX_LENGTH, type CommentAnchor } from '@domain/schemas/comment.schema';
import { BLOCK_LEVEL_TAGS, isMentionElement, MENTION_SENTINEL, SKIPPED_TAGS } from '@root/helpers/comments/anchor-text';

export interface TextSegment {
  node: Text;
  start: number;
  end: number;
}

export interface MentionSegment {
  element: Element;
  start: number;
}

export interface AnchorTextMap {
  text: string;
  segments: TextSegment[];
  mentions: MentionSegment[];
}

/** Same rules as `anchorTextOf` (helpers/comments/anchor-text.ts), over live DOM — pinned by a parity test. */
export const collectAnchorText = (root: Node): AnchorTextMap => {
  let text = '';
  const segments: TextSegment[] = [];
  const mentions: MentionSegment[] = [];

  const walk = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const { data } = node as Text;
      segments.push({ node: node as Text, start: text.length, end: text.length + data.length });
      text += data;
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }
    const element = node as Element;
    const tag = element.tagName.toLowerCase();
    if (SKIPPED_TAGS.has(tag)) {
      return;
    }
    if (isMentionElement(element.getAttribute('data-type'))) {
      mentions.push({ element, start: text.length });
      text += MENTION_SENTINEL;
      return;
    }
    if (tag === 'br') {
      text += '\n';
      return;
    }
    element.childNodes.forEach(walk);
    if (BLOCK_LEVEL_TAGS.has(tag)) {
      text += '\n';
    }
  };

  root.childNodes.forEach(walk);
  return { text, segments, mentions };
};

type Point = { node: Node; start: number; end: number };

const orderedPoints = (map: AnchorTextMap): Point[] =>
  [
    ...map.segments.map((segment) => ({ node: segment.node as Node, start: segment.start, end: segment.end })),
    ...map.mentions.map((mention) => ({ node: mention.element as Node, start: mention.start, end: mention.start + 1 })),
  ].sort((a, b) => a.start - b.start);

const isAtOrInside = (container: Node, node: Node): boolean => container === node || container.contains(node);
const follows = (anchor: Node, node: Node): boolean =>
  Boolean(anchor.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING);
const precedes = (anchor: Node, node: Node): boolean =>
  Boolean(anchor.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_PRECEDING);

const positionOf = (
  map: AnchorTextMap,
  root: Element,
  container: Node,
  offset: number,
  edge: 'start' | 'end',
): number | null => {
  const element = container.nodeType === Node.ELEMENT_NODE ? (container as Element) : container.parentElement;
  const mentionElement = element?.closest('[data-type="mention"]');
  if (mentionElement && root.contains(mentionElement)) {
    const mention = map.mentions.find((item) => item.element === mentionElement);
    if (mention) {
      return edge === 'start' ? mention.start : mention.start + 1;
    }
  }

  if (container.nodeType === Node.TEXT_NODE) {
    const segment = map.segments.find((item) => item.node === container);
    return segment ? segment.start + Math.min(offset, segment.end - segment.start) : null;
  }

  const points = orderedPoints(map);
  const children = container.childNodes;

  if (edge === 'start') {
    if (offset >= children.length) {
      const next = points.find((item) => follows(container, item.node) && !container.contains(item.node));
      return next ? next.start : map.text.length;
    }
    const child = children[offset];
    const point = points.find((item) => isAtOrInside(child, item.node) || follows(child, item.node));
    return point ? point.start : map.text.length;
  }

  if (offset === 0) {
    const point = points.find((item) => isAtOrInside(container, item.node) || follows(container, item.node));
    return point ? point.start : 0;
  }
  const child = children[offset - 1];
  const point = [...points].reverse().find((item) => isAtOrInside(child, item.node) || precedes(child, item.node));
  return point ? point.end : 0;
};

/** Maps a DOM selection inside one block's content to a comment anchor; null when it cannot be anchored. */
export const rangeToAnchor = (range: Range, root: Element): CommentAnchor | null => {
  if (range.collapsed || !root.contains(range.startContainer) || !root.contains(range.endContainer)) {
    return null;
  }

  const map = collectAnchorText(root);
  const rawStart = positionOf(map, root, range.startContainer, range.startOffset, 'start');
  const rawEnd = positionOf(map, root, range.endContainer, range.endOffset, 'end');
  if (rawStart === null || rawEnd === null) {
    return null;
  }

  let start = rawStart;
  let end = rawEnd;
  while (start < end && /\s/.test(map.text[start])) {
    start += 1;
  }
  while (end > start && /\s/.test(map.text[end - 1])) {
    end -= 1;
  }
  if (start >= end) {
    return null;
  }

  const quote = quoteOf(map, start, end);
  if (!quote) {
    return null;
  }

  // The quote is display-only, so a long selection is clipped rather than rejected by the API.
  return { start, end, text: map.text.slice(start, end), quote: quote.slice(0, COMMENT_QUOTE_MAX_LENGTH) };
};

/** The text runs a datapoint shows; the thread mark and the quote both follow these, so they cannot drift apart. */
export const mentionTextNodes = (mention: Element): Text[] => {
  const nodes: Text[] = [];
  const walker = document.createTreeWalker(mention, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if ((node as Text).data.trim()) {
      nodes.push(node as Text);
    }
  }
  return nodes;
};

const visibleText = (element: Element): string =>
  mentionTextNodes(element)
    .map((node) => node.data.trim())
    .join(' ');

// The quote describes what the mark covers, not what the mouse touched: a boundary inside a datapoint has already
// snapped outward, so a datapoint stands in for its sentinel with everything it shows (a schedule's rows joined).
const quoteOf = (map: AnchorTextMap, start: number, end: number): string => {
  let quote = '';
  let cursor = start;
  map.mentions.forEach((mention) => {
    if (mention.start < start || mention.start >= end) {
      return;
    }
    quote += map.text.slice(cursor, mention.start) + visibleText(mention.element);
    cursor = mention.start + 1;
  });
  return (quote + map.text.slice(cursor, end)).replace(/\s+/g, ' ').trim();
};
